import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import useI18n from '../../i18n/useI18n'
import Modal from '../../components/modal'
import Input from '../../components/input'
import Select from '../../components/select'
import Editor from '../../components/editor'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import general from '../../utils/general'
import EventBus from '../../infrastructure/event-bus/event-bus'

const AnnouncementFormModal = ({ isOpen, announcement, onClose, onSuccess }) => {
  const dispatch = useDispatch()
  const { t } = useI18n()
  const isEditMode = !!announcement?.id

  const [formData, setFormData] = useState({
    title: '',
    titleEn: '',
    titleRu: '',
    content: '',
    announcementType: '',
    targetUserType: 'all',
    targetAgentIds: [],
    targetStudentIds: [],
    isPublished: false,
    expiresAt: '',
    priority: 0,
    link: ''
  })

  const [validationErrors, setValidationErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedAgentIds, setSelectedAgentIds] = useState([])
  const [selectedStudentIds, setSelectedStudentIds] = useState([])
  const [agents, setAgents] = useState([])
  const [students, setStudents] = useState([])
  const [loadingAgents, setLoadingAgents] = useState(false)
  const [loadingStudents, setLoadingStudents] = useState(false)

  useEffect(() => {
    if (isOpen && announcement?.id) {
      // Edit mode - fetch full details or use provided data
      if (announcement.title && announcement.content) {
        mapAnnouncementToForm(announcement)
      } else {
        fetchAnnouncementDetails()
      }
    } else if (isOpen && !announcement) {
      // Create mode - reset form
      resetForm()
    }

    if (!isOpen) {
      resetForm()
    }
  }, [isOpen, announcement?.id])

  useEffect(() => {
    if (isOpen) {
      fetchAgents()
      fetchStudents()
    }
  }, [isOpen])

  const fetchAgents = () => {
    setLoadingAgents(true)
    dispatch(
      ApiCallActions.Get({
        url: 'agents/active',
        showAlertOnError: false,
        showLoading: false,
        onSuccess: ({ data }) => {
          const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []
          setAgents(list)
          setLoadingAgents(false)
        },
        onError: () => {
          setAgents([])
          setLoadingAgents(false)
        }
      })
    )
  }

  const fetchStudents = () => {
    setLoadingStudents(true)
    dispatch(
      ApiCallActions.Get({
        url: 'students',
        query: { limit: 99 },
        showAlertOnError: false,
        showLoading: false,
        onSuccess: ({ data }) => {
          const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []
          setStudents(list)
          setLoadingStudents(false)
        },
        onError: () => {
          setStudents([])
          setLoadingStudents(false)
        }
      })
    )
  }

  const resetForm = () => {
    setFormData({
      title: '',
      titleEn: '',
      titleRu: '',
      content: '',
      announcementType: '',
      targetUserType: 'all',
      targetAgentIds: [],
      targetStudentIds: [],
      isPublished: false,
      expiresAt: '',
      priority: 0,
      link: ''
    })
    setValidationErrors({})
    setSelectedAgentIds([])
    setSelectedStudentIds([])
  }

  const fetchAnnouncementDetails = () => {
    setLoading(true)
    dispatch(
      ApiCallActions.Get({
        url: `announcements/${announcement.id}`,
        showAlertOnError: true,
        showLoading: false,
        onSuccess: ({ data }) => {
          const payload = data?.data || data || null
          if (payload) {
            mapAnnouncementToForm(payload)
          }
          setLoading(false)
        },
        onError: ({ errorMessage }) => {
          if (errorMessage) {
            general.notificationError(errorMessage)
          }
          setLoading(false)
        }
      })
    )
  }

  const mapAnnouncementToForm = (annData) => {
    const formatDateTimeForInput = (dateString) => {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        return localDateTime.toISOString().slice(0, 16)
      } catch {
        return dateString
      }
    }

    setFormData({
      title: annData.title || '',
      titleEn: annData.titleEn || '',
      titleRu: annData.titleRu || '',
      content: annData.content || '',
      announcementType: annData.announcementType || '',
      targetUserType: annData.targetUserType || 'all',
      targetAgentIds: annData.targetAgentIds || [],
      targetStudentIds: annData.targetStudentIds || [],
      isPublished: annData.isPublished !== undefined ? annData.isPublished : false,
      expiresAt: formatDateTimeForInput(annData.expiresAt),
      priority: annData.priority || 0,
      link: annData.link || ''
    })

    setSelectedAgentIds(annData.targetAgentIds || [])
    setSelectedStudentIds(annData.targetStudentIds || [])
  }

  const clearValidationError = (fieldName) => {
    if (validationErrors[fieldName]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[fieldName]
        return newErrors
      })
    }
  }

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
    clearValidationError(name)
  }

  const handleAnnouncementTypeChange = (value) => {
    setFormData((prev) => {
      const nextState = {
        ...prev,
        announcementType: value,
        targetUserType: value === 'agent' ? 'agent' : value === 'student' ? 'student' : 'all',
        targetAgentIds: value === 'agent' ? prev.targetAgentIds : [],
        targetStudentIds: value === 'student' ? prev.targetStudentIds : []
      }
      return nextState
    })

    if (value !== 'agent') {
      setSelectedAgentIds([])
    }

    if (value !== 'student') {
      setSelectedStudentIds([])
    }

    clearValidationError('announcementType')
  }

  const handleAgentSelectChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value).filter(Boolean)
    setSelectedAgentIds(selectedOptions)
    setFormData(prev => ({
      ...prev,
      targetAgentIds: selectedOptions
    }))
  }

  const handleStudentSelectChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value).filter(Boolean)
    setSelectedStudentIds(selectedOptions)
    setFormData(prev => ({
      ...prev,
      targetStudentIds: selectedOptions
    }))
  }

  const validateForm = () => {
    const errors = {}

    if (!formData.title || formData.title.trim() === '') {
      errors.title = t('ANNOUNCEMENTS_FORM_VALIDATION_TITLE_REQUIRED') || 'Başlık zorunludur'
    }

    if (!formData.content || formData.content.trim() === '') {
      errors.content = t('ANNOUNCEMENTS_FORM_VALIDATION_CONTENT_REQUIRED') || 'İçerik zorunludur'
    }

    if (!formData.announcementType || formData.announcementType.trim() === '') {
      errors.announcementType = t('ANNOUNCEMENTS_FORM_VALIDATION_TYPE_REQUIRED') || 'Duyuru tipi zorunludur'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const buildSubmitData = () => {
    const submitData = {
      title: formData.title.trim(),
      titleEn: formData.titleEn?.trim() || '',
      titleRu: formData.titleRu?.trim() || '',
      content: formData.content.trim(),
      announcementType: formData.announcementType,
      targetUserType: formData.targetUserType || 'all',
      isPublished: formData.isPublished,
      priority: parseInt(formData.priority, 10) || 0,
      link: formData.link?.trim() || ''
    }

    if (formData.expiresAt) {
      submitData.expiresAt = new Date(formData.expiresAt).toISOString()
    }

    if (formData.targetAgentIds && formData.targetAgentIds.length > 0) {
      submitData.targetAgentIds = formData.targetAgentIds
    }

    if (formData.targetStudentIds && formData.targetStudentIds.length > 0) {
      submitData.targetStudentIds = formData.targetStudentIds
    }

    return submitData
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    const payload = buildSubmitData()

    const apiAction = isEditMode
      ? ApiCallActions.Put({
          url: `announcements/${announcement.id}`,
          data: payload,
          showAlertOnError: true,
          showLoading: true,
          onSuccess: ({ data }) => {
            const updatedData = data?.data || data
            
            EventBus.sc.onEntityUpdated({
              entityType: 'announcement',
              entityId: announcement.id,
              entityData: updatedData
            })

            general.messageSuccess(t('ANNOUNCEMENTS_FORM_UPDATE_SUCCESS') || 'Duyuru başarıyla güncellendi')
            setIsSubmitting(false)
            
            if (onSuccess) {
              onSuccess(updatedData)
            }
            
            onClose()
          },
          onError: ({ errorMessage }) => {
            general.notificationError(errorMessage || t('ANNOUNCEMENTS_FORM_UPDATE_ERROR') || 'Duyuru güncellenirken bir hata oluştu')
            setIsSubmitting(false)
          }
        })
      : ApiCallActions.Post({
          url: 'announcements',
          data: payload,
          showAlertOnError: true,
          showLoading: true,
          onSuccess: ({ data }) => {
            const newData = data?.data || data
            
            EventBus.sc.onEntityCreated({
              entityType: 'announcement',
              entityData: newData
            })

            general.messageSuccess(t('ANNOUNCEMENTS_FORM_CREATE_SUCCESS') || 'Duyuru başarıyla oluşturuldu')
            setIsSubmitting(false)
            
            if (onSuccess) {
              onSuccess(newData)
            }
            
            onClose()
          },
          onError: ({ errorMessage }) => {
            general.notificationError(errorMessage || t('ANNOUNCEMENTS_FORM_CREATE_ERROR') || 'Duyuru oluşturulurken bir hata oluştu')
            setIsSubmitting(false)
          }
        })

    dispatch(apiAction)
  }

  const announcementTypeOptions = [
    { value: 'general', label: t('ANNOUNCEMENTS_TYPE_GENERAL') || 'Genel' },
    { value: 'agent', label: t('ANNOUNCEMENTS_TYPE_AGENT') || 'Acente' },
    { value: 'student', label: t('ANNOUNCEMENTS_TYPE_STUDENT') || 'Öğrenci' }
  ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? (t('ANNOUNCEMENTS_FORM_TITLE_EDIT') || 'Duyuru Düzenle') : (t('ANNOUNCEMENTS_FORM_TITLE_CREATE') || 'Yeni Duyuru')}
      subtitle={isEditMode ? (t('ANNOUNCEMENTS_FORM_SUBTITLE_EDIT') || 'Duyuru bilgilerini düzenleyin') : (t('ANNOUNCEMENTS_FORM_SUBTITLE_CREATE') || 'Yeni bir duyuru oluşturun')}
      icon="zmdi-notifications-active"
      size="xlarge"
    >
      {loading ? (
        <div className="text-center p-4">
          <p>{t('ANNOUNCEMENTS_DETAIL_LOADING') || 'Yükleniyor...'}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {/* Temel Bilgiler */}
          <div className="card m-b-20">
            <div className="header">
              <h2>
                <i className="zmdi zmdi-info m-r-5"></i>
                {t('ANNOUNCEMENTS_FORM_SECTION_BASIC') || 'Temel Bilgiler'}
              </h2>
            </div>
            <div className="body">
              <div className="row">
                <div className="col-md-12">
                  <div className="form-group">
                    <label>{t('ANNOUNCEMENTS_FORM_FIELD_TITLE') || 'Başlık (Türkçe)'} <span className="text-danger">*</span></label>
                    <Input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      placeholder={t('ANNOUNCEMENTS_FORM_FIELD_TITLE_PLACEHOLDER') || 'Duyuru başlığını girin'}
                      icon="zmdi-collection-text"
                      required
                      errorMessage={validationErrors.title}
                    />
                    {validationErrors.title && (
                      <small className="text-danger">{validationErrors.title}</small>
                    )}
                  </div>
                </div>
              </div>

              <div className="row m-t-15">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>{t('ANNOUNCEMENTS_FORM_FIELD_TITLE_EN') || 'Başlık (İngilizce)'}</label>
                    <Input
                      type="text"
                      name="titleEn"
                      value={formData.titleEn}
                      onChange={(e) => handleChange('titleEn', e.target.value)}
                      placeholder={t('ANNOUNCEMENTS_FORM_FIELD_TITLE_EN_PLACEHOLDER') || 'Announcement title (English)'}
                      icon="zmdi-collection-text"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>{t('ANNOUNCEMENTS_FORM_FIELD_TITLE_RU') || 'Başlık (Rusça)'}</label>
                    <Input
                      type="text"
                      name="titleRu"
                      value={formData.titleRu}
                      onChange={(e) => handleChange('titleRu', e.target.value)}
                      placeholder={t('ANNOUNCEMENTS_FORM_FIELD_TITLE_RU_PLACEHOLDER') || 'Заголовок объявления (Russian)'}
                      icon="zmdi-collection-text"
                    />
                  </div>
                </div>
              </div>

              <div className="row m-t-15">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>{t('ANNOUNCEMENTS_FORM_FIELD_TYPE') || 'Duyuru Tipi'} <span className="text-danger">*</span></label>
                    <Select
                      name="announcementType"
                      value={formData.announcementType}
                      onChange={(e) => handleAnnouncementTypeChange(e.target.value)}
                      placeholder={t('ANNOUNCEMENTS_FORM_FIELD_TYPE_PLACEHOLDER') || 'Duyuru tipini seçin'}
                      options={announcementTypeOptions}
                      required
                      icon="zmdi-label"
                    />
                    {validationErrors.announcementType && (
                      <small className="text-danger">{validationErrors.announcementType}</small>
                    )}
                  </div>
                </div>
              </div>

              <div className="row m-t-15">
                <div className="col-md-12">
                  <div className="form-group">
                    <label>{t('ANNOUNCEMENTS_FORM_FIELD_CONTENT') || 'İçerik'} <span className="text-danger">*</span></label>
                    <Editor
                      value={formData.content}
                      onChange={(data) => {
                        handleChange('content', data)
                      }}
                      placeholder={t('ANNOUNCEMENTS_FORM_FIELD_CONTENT_PLACEHOLDER') || 'Duyuru içeriğini yazın...'}
                      minHeight={300}
                    />
                    
                    {validationErrors.content && (
                      <small className="text-danger">{validationErrors.content}</small>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Hedef Kullanıcılar */}
          {formData.announcementType === 'agent' && (
            <div className="card m-b-20">
              <div className="header">
                <h2>
                  <i className="zmdi zmdi-accounts m-r-5"></i>
                  {t('ANNOUNCEMENTS_FORM_SECTION_TARGET_AGENTS') || 'Hedef Acenteler'}
                </h2>
              </div>
              <div className="body">
                <div className="form-group">
                  <label>{t('ANNOUNCEMENTS_FORM_FIELD_TARGET_AGENTS') || 'Acenteler (Boş bırakılırsa tüm acentelere gönderilir)'}</label>
                  <select
                    name="targetAgentIds"
                    value={selectedAgentIds}
                    onChange={handleAgentSelectChange}
                    className="form-control"
                    multiple
                    style={{ minHeight: '150px' }}
                    disabled={loadingAgents}
                  >
                    {loadingAgents ? (
                      <option disabled>Yükleniyor...</option>
                    ) : agents.length === 0 ? (
                      <option disabled>Acente bulunamadı</option>
                    ) : (
                      agents.map((agent) => {
                        const id = agent?.id?.toString()
                        const label = agent?.companyName || agent?.name || agent?.fullName || ''
                        if (!id || !label) return null
                        return (
                          <option key={id} value={id}>
                            {label}
                          </option>
                        )
                      })
                    )}
                  </select>
                  {selectedAgentIds.length > 0 && (
                    <small className="text-muted m-t-5">
                      {selectedAgentIds.length} {t('ANNOUNCEMENTS_FORM_SELECTED_AGENTS') || 'acente seçildi'}
                    </small>
                  )}
                  {!loadingAgents && agents.length > 0 && (
                    <small className="text-muted d-block m-t-5">
                      {t('ANNOUNCEMENTS_FORM_FIELD_TARGET_AGENTS_HELP') || 'Çoklu seçim için Ctrl (Windows) veya Cmd (Mac) tuşuna basılı tutun'}
                    </small>
                  )}
                </div>
              </div>
            </div>
          )}

          {formData.announcementType === 'student' && (
            <div className="card m-b-20">
              <div className="header">
                <h2>
                  <i className="zmdi zmdi-account-circle m-r-5"></i>
                  {t('ANNOUNCEMENTS_FORM_SECTION_TARGET_STUDENTS') || 'Hedef Öğrenciler'}
                </h2>
              </div>
              <div className="body">
                <div className="form-group">
                  <label>{t('ANNOUNCEMENTS_FORM_FIELD_TARGET_STUDENTS') || 'Öğrenciler (Boş bırakılırsa tüm öğrencilere gönderilir)'}</label>
                  <select
                    name="targetStudentIds"
                    value={selectedStudentIds}
                    onChange={handleStudentSelectChange}
                    className="form-control"
                    multiple
                    style={{ minHeight: '150px' }}
                    disabled={loadingStudents}
                  >
                    {loadingStudents ? (
                      <option disabled>Yükleniyor...</option>
                    ) : students.length === 0 ? (
                      <option disabled>Öğrenci bulunamadı</option>
                    ) : (
                      students.map((student) => {
                        const id = student?.id?.toString()
                        const firstName = student?.user?.firstName || ''
                        const lastName = student?.user?.lastName || ''
                        const label = `${firstName} ${lastName}`.trim() || student?.user?.email || ''
                        if (!id || !label) return null
                        return (
                          <option key={id} value={id}>
                            {label}
                          </option>
                        )
                      })
                    )}
                  </select>
                  {selectedStudentIds.length > 0 && (
                    <small className="text-muted m-t-5">
                      {selectedStudentIds.length} {t('ANNOUNCEMENTS_FORM_SELECTED_STUDENTS') || 'öğrenci seçildi'}
                    </small>
                  )}
                  {!loadingStudents && students.length > 0 && (
                    <small className="text-muted d-block m-t-5">
                      {t('ANNOUNCEMENTS_FORM_FIELD_TARGET_STUDENTS_HELP') || 'Çoklu seçim için Ctrl (Windows) veya Cmd (Mac) tuşuna basılı tutun'}
                    </small>
                  )}
                </div>
              </div>
            </div>
          )}

          {(!formData.announcementType || formData.announcementType === 'general') && (
            <div className="alert alert-info m-b-20">
              <i className="zmdi zmdi-info-outline m-r-5"></i>
              {t('ANNOUNCEMENTS_FORM_GENERAL_HINT') || 'Genel duyurular tüm kullanıcılarla paylaşılır.'}
            </div>
          )}

          {/* Ayarlar */}
          <div className="card m-b-20">
            <div className="header">
              <h2>
                <i className="zmdi zmdi-settings m-r-5"></i>
                {t('ANNOUNCEMENTS_FORM_SECTION_SETTINGS') || 'Ayarlar'}
              </h2>
            </div>
            <div className="body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>{t('ANNOUNCEMENTS_FORM_FIELD_EXPIRES_AT') || 'Son Geçerlilik Tarihi'}</label>
                    <Input
                      type="datetime-local"
                      name="expiresAt"
                      value={formData.expiresAt}
                      onChange={(e) => handleChange('expiresAt', e.target.value)}
                      placeholder={t('ANNOUNCEMENTS_FORM_FIELD_EXPIRES_AT_PLACEHOLDER') || 'Son geçerlilik tarihi'}
                      icon="zmdi-calendar-close"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>{t('ANNOUNCEMENTS_FORM_FIELD_PRIORITY') || 'Öncelik'}</label>
                    <Input
                      type="number"
                      name="priority"
                      value={formData.priority}
                      onChange={(e) => handleChange('priority', e.target.value)}
                      placeholder={t('ANNOUNCEMENTS_FORM_FIELD_PRIORITY_PLACEHOLDER') || 'Öncelik (0-100)'}
                      icon="zmdi-star"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
              </div>

              <div className="row m-t-15">
                <div className="col-md-12">
                  <div className="form-group">
                    <label>{t('ANNOUNCEMENTS_FORM_FIELD_LINK_URL') || 'Link (URL)'}</label>
                    <Input
                      type="url"
                      name="link"
                      value={formData.link}
                      onChange={(e) => handleChange('link', e.target.value)}
                      placeholder={t('ANNOUNCEMENTS_FORM_FIELD_LINK_URL_PLACEHOLDER') || 'https://...'}
                      icon="zmdi-link"
                    />
                    <small className="text-muted d-block m-t-5">
                      {t('ANNOUNCEMENTS_FORM_FIELD_LINK_URL_HELP') || 'Duyuruya eklemek istediğiniz bağlantı adresi'}
                    </small>
                  </div>
                </div>
              </div>

              <div className="row m-t-15">
                <div className="col-md-12">
                  <div className="form-group">
                    <div className="form-check">
                      <input
                        id="announcement-published-toggle"
                        type="checkbox"
                        className="form-check-input"
                        checked={formData.isPublished}
                        onChange={(e) => handleChange('isPublished', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="announcement-published-toggle">
                        {t('ANNOUNCEMENTS_FORM_FIELD_IS_PUBLISHED') || 'Yayınla'}
                      </label>
                    </div>
                    <small className="text-muted d-block m-t-5">
                      {t('ANNOUNCEMENTS_FORM_FIELD_IS_PUBLISHED_HELP') || 'İşaretlenirse duyuru hemen yayınlanır'}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row m-t-20">
            <div className="col-12">
              <div className="d-flex justify-content-end" style={{ gap: '10px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  {t('ANNOUNCEMENTS_FORM_BUTTON_CANCEL') || 'İptal'}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting 
                    ? (isEditMode 
                        ? (t('ANNOUNCEMENTS_FORM_BUTTON_UPDATING') || 'Güncelleniyor...') 
                        : (t('ANNOUNCEMENTS_FORM_BUTTON_CREATING') || 'Oluşturuluyor...'))
                    : (isEditMode 
                        ? (t('ANNOUNCEMENTS_FORM_BUTTON_UPDATE') || 'Güncelle') 
                        : (t('ANNOUNCEMENTS_FORM_BUTTON_CREATE') || 'Oluştur'))
                  }
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </Modal>
  )
}

export default AnnouncementFormModal

